import React, { useRef } from "react";
import SecurityModal from "./SecurityModal";
import Modal from "./Modal";
import SubmitHookForm from "./SubmitHookForm";
import CloseIconButton from "./CloseIconButton";
import CancelButton from "./CancelButton";
import { Controller } from "react-hook-form";
import SelectHookFormSingle from "./SelectHookFormSingle";
import TextInputLabelHookForm from "./TextInputLabelHookForm";
import { RequiredInput } from "modules/CarrierAgreementMgmt/Security/atoms/atoms";
import { useRecoilState } from "recoil";
import { toast } from "./toastNotification";
import { SecurityFireAlarmSystemCheckBox } from "./SecurityFireAlarmSystemCheckBox";

interface SecurityFireAlarmSystemFormProps {
  isOpen: boolean;
  onClose: () => void;
  mode: "initialAdd" | "add" | "edit" | "initialEdit";
  isSaving?: boolean;
  onSubmit: (data: any) => void;
  formMethods: any;

  vendorsOption: any[];
  dataConnOption: any[];
  alarmTypeOption: any[];
  fireAlarmSensorOptions: any[];
  floorCountOptions: any[];

  hasDeletePermission?: boolean;
  isEditState?: boolean;
  isPhoneConnectionRequired?: boolean;
}

const SecurityFireAlarmSystemForm: React.FC<
  SecurityFireAlarmSystemFormProps
> = ({
  isOpen,
  onClose,
  mode,
  isSaving = false,
  onSubmit,
  formMethods,
  vendorsOption,
  dataConnOption,
  alarmTypeOption,
  fireAlarmSensorOptions,
  floorCountOptions,
  hasDeletePermission,
  isEditState,
  isPhoneConnectionRequired,
}) => {
  const ModalComponent =
    mode === "initialAdd" || mode === "add" ? SecurityModal : Modal;
  const isToastVisible = useRef(false);
  const getTitle = () => {
    switch (mode) {
      case "initialAdd":
      case "add":
        return "Add Fire Alarm System";
      case "edit":
      case "initialEdit":
        return "Edit Fire Alarm System";
      default:
        return "Fire Alarm System Form";
    }
  };

  const getButtonText = () => {
    if (isSaving) return "Saving..";
    switch (mode) {
      case "initialAdd":
      case "initialEdit":
        return "Proceed";
      case "add":
      case "edit":
        return "Save";
      default:
        return "Submit";
    }
  };

  const getFieldName = (fieldName: string) => {
    return mode === "initialAdd" || mode === "add"
      ? `fireAlarmSystem[0].${fieldName}`
      : fieldName;
  };

  const phoneNumberConverter = (phoneNumber: string): string => {
    // const match = phoneNumber
    //   .replaceAll(/\D/g, "")
    const regex = /(\d{0,3})(\d{0,3})(\d{0,4})/;
    const match = regex.exec(phoneNumber.replaceAll(/\D/g, ""));
    if (!match) return "";

    const first = match[1];
    const second = match[2];
    const third = match[3];

    if (!first) return "";
    else if (!second) return `${first}`;
    else if (!third) return `${first}-${second}`;
    return `${first}-${second}-${third}`;
  };

  const showCloseIcon =
    mode === "initialAdd" || mode === "add" || mode === "initialEdit";
  const showCancelButton = mode === "edit";
  const [isRequired, setIsRequired] = useRecoilState(RequiredInput);
  const { control } = formMethods;

  return (
    <ModalComponent
      isCloseHide={true}
      modalFilterStat=""
      className="max-w-[1200px]"
      width=""
      title=""
      isOpen={isOpen}
      onClose={undefined}
      hasCloseButton={false}
    >
      {/* Header */}
      <div className="flex flex-row whitespace-nowrap justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
        <div className="ml-2 font-bold text-primary text-md">{getTitle()}</div>

        <div className="flex justify-between gap-2 mr-2">
          <SubmitHookForm
            type="button"
            disabled={false}
            buttonText={getButtonText()}
            className={undefined}
            onClick={onSubmit}
          />
          {showCloseIcon && (
            <div className="relative -top-2 -right-6">
              <CloseIconButton
                onClick={() => {
                  setIsRequired(false);
                  onClose();
                }}
              />
            </div>
          )}

          {showCancelButton && (
            <CancelButton
              onClick={() => {
                setIsRequired(false);
                onClose();
              }}
            />
          )}
        </div>
      </div>

      <div className="w-full border-b-2 border-secondary mb-3"></div>

      {(mode === "initialAdd" || mode === "add") && (
        <div className="text-xs text-secondary italic font-normal m-2">
          <b>Note:</b> All fields with * are required to fill.
        </div>
      )}

      {/* Form Content */}
      <div className="box-shadow rounded-md modal-content">
        <div className="grid grid-cols-1 md:grid-cols-2 mx-4 gap-6 pb-4">
          {/* Left Column */}
          <div className="space-y-1">
            <Controller
              name={getFieldName("vendorId")}
              control={control}
              rules={{ required: "Vendor is Required" }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => {
                    field.onChange(e ? e.value : null);
                    formMethods.setValue(
                      getFieldName("vendorPhone"),
                      e ? e.vendorPhone : null
                    );
                    formMethods.setValue(
                      getFieldName("vendorAccountNumber"),
                      e ? e.accountNumber : null
                    );
                  }}
                  value={field.value}
                  options={vendorsOption}
                  label="Vendor Name"
                  className={
                    isRequired ? "border-2 border-red-500 col-span-2" : ""
                  }
                  required={true}
                />
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("vendorPhone")}
              label="Vendor Phone"
              type="text"
              register={formMethods.register(getFieldName("vendorPhone"), {})}
              maxLength={50}
              disabled={true}
            />

            <TextInputLabelHookForm
              name={getFieldName("vendorAccountNumber")}
              label="Account Number"
              type="text"
              register={formMethods.register(
                getFieldName("vendorAccountNumber"),
                {}
              )}
              maxLength={50}
              disabled={true}
            />

            <TextInputLabelHookForm
              name={getFieldName("siteNumber")}
              label="Site Number"
              type="text"
              register={formMethods.register(getFieldName("siteNumber"), {})}
              maxLength={100}
            />

            <Controller
              name={getFieldName("dataConnectionId")}
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <div className="w-full">
                  <div className="sm:w-full md:w-full lg:w-full gap-y-4">
                    <SelectHookFormSingle
                      field={field}
                      onChange={(e) => field.onChange(e ? e.value : null)}
                      value={field.value}
                      options={dataConnOption}
                      label="Data Connection"
                      className={
                        formMethods.formState.errors.dataConnectionId &&
                        "border-2 border-red-500 col-span-2"
                      }
                      required={false}
                    />
                  </div>
                </div>
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("installDate")}
              label="Install Date"
              type="date"
              register={formMethods.register(getFieldName("installDate"), {})}
            />

            <TextInputLabelHookForm
              name={getFieldName("ipaddress")}
              label="IP Address"
              type="text"
              register={formMethods.register(getFieldName("ipaddress"), {})}
              maxLength={100}
            />

            <TextInputLabelHookForm
              name={getFieldName("ipGateway")}
              label="IP Gateway"
              type="text"
              register={formMethods.register(getFieldName("ipGateway"), {})}
              maxLength={100}
            />

            <TextInputLabelHookForm
              name={getFieldName("subnetMask")}
              label="Subnet Mask"
              type="text"
              register={formMethods.register(getFieldName("subnetMask"), {})}
              maxLength={100}
            />

            <TextInputLabelHookForm
              name={getFieldName("dns")}
              label="DNS"
              type="text"
              register={formMethods.register(getFieldName("dns"), {})}
              maxLength={100}
            />
          </div>

          {/* Right Column */}
          <div
            className={`space-y-1 ${
              mode === "initialAdd" || mode === "add" ? "mr-4" : ""
            }`}
          >
            <TextInputLabelHookForm
              name={getFieldName("phoneConnection")}
              className={
                isPhoneConnectionRequired
                  ? "border-2 border-red-500 col-span-2"
                  : "border border-gray-300 col-span-2"
              }
              label="Phone Connection"
              type="text"
              register={formMethods.register(
                getFieldName("phoneConnection"),
                {}
              )}
              onChange={(e) => {
                const regIsDecimal = /^\d+$/;

                const { value } = e.target;
                const inputCharacter = value.at(-1);
                const isInvalidChar =
                  inputCharacter &&
                  !regIsDecimal.test(inputCharacter) &&
                  inputCharacter !== "-";

                if (isInvalidChar) {
                  if (!isToastVisible.current) {
                    isToastVisible.current = true;

                    toast
                      .fire({
                        icon: "info",
                        title: "Phone Number Format",
                        text: "Please enter numbers only, formatting is automatic.",
                      })
                      .then(() => {
                        isToastVisible.current = false;
                      });
                  }
                }

                formMethods.setValue(
                  getFieldName("phoneConnection"),
                  phoneNumberConverter(value),
                  { shouldValidate: true, shouldDirty: true }
                );
              }}
              maxLength={12}
            />

            <TextInputLabelHookForm
              name={getFieldName("connectionDetails")}
              label="Connection Details"
              type="text"
              register={formMethods.register(
                getFieldName("connectionDetails"),
                {}
              )}
              maxLength={100}
            />

            <Controller
              name={getFieldName("fireAlarmTypeId")}
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e ? e.value : null)}
                  value={field.value}
                  options={alarmTypeOption}
                  label="Fire Alarm Type"
                  className={
                    formMethods.formState.errors.fireAlarmTypeId &&
                    "border-2 border-red-500 col-span-2"
                  }
                  required={false}
                />
              )}
            />

            <Controller
              name={getFieldName("numberOfFireAlarmSensorsId")}
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e ? e.value : null)}
                  value={field.value}
                  options={fireAlarmSensorOptions}
                  label="Number Of Sensors"
                  className={
                    formMethods.formState.errors.numberOfFireAlarmSensorsId &&
                    "border-2 border-red-500 col-span-2"
                  }
                  required={false}
                />
              )}
            />

            <Controller
              name={getFieldName("numberOfFloorsId")}
              control={formMethods.control}
              rules={{ required: false }}
              render={({ field }) => (
                <SelectHookFormSingle
                  field={field}
                  onChange={(e) => field.onChange(e ? e.value : null)}
                  value={field.value}
                  options={floorCountOptions}
                  label="Number Of Floors"
                  className={
                    formMethods.formState.errors.numberOfFloorsId &&
                    "border-2 border-red-500 col-span-2"
                  }
                  required={false}
                />
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("specialEquipment")}
              label="Any Additional Special Equipment"
              type="text"
              register={formMethods.register(
                getFieldName("specialEquipment"),
                {}
              )}
              maxLength={150}
            />

            <TextInputLabelHookForm
              name={getFieldName("cancellationDate")}
              label="Cancellation Date"
              type="date"
              register={formMethods.register(
                getFieldName("cancellationDate"),
                {}
              )}
            />

            <TextInputLabelHookForm
              name={getFieldName("portNumber")}
              label="Port Number"
              type="text"
              register={formMethods.register(getFieldName("portNumber"), {})}
              maxLength={100}
            />

            {/* only active if edit*/}
            {mode === "edit" && isEditState && hasDeletePermission && (
              <SecurityFireAlarmSystemCheckBox
                control={control}
                label="Active Account"
                name="isActive"
              />
            )}
          </div>
        </div>

        {(mode === "initialAdd" || mode === "add") && (
          <div>
            <div className="w-full border-b-2 border-grey-200"></div>
          </div>
        )}
      </div>
    </ModalComponent>
  );
};

export default SecurityFireAlarmSystemForm;
